import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../../contexts/I18nProvider';
import { useMaxLineItemQuantity } from '../../../hooks/useMaxLineItemQuantity';

export type NumericInputMode = 'integer' | 'decimal';

interface NumericInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'inputMode' | 'pattern'> {
  /**
   * 'integer' -> kun heltall (tastatur uten komma/punktum)
   * 'decimal' -> desimaltall (tastatur med komma/punktum) [default]
   */
  mode?: NumericInputMode;
  /**
   * Feltet er et vareantall (plukk, mottak, flytt, telling, korreksjon ...).
   * Sperrer da verdier over «Maks antall per linje» (SystemSetup.MaxLineItemQuantity),
   * f.eks. en EAN skannet inn i antallsfeltet. Se «Antallssperre» i doc under.
   * Ikke sett den på vekt, volum, mål, ID-er o.l.
   */
  quantity?: boolean;
}

/** Tegn med kortere mellomrom enn dette regnes som samme «utbrudd» (skanner/rask tasting). */
const BURST_GAP_MS = 120;
/** Etter en sperre avvises videre input til det har vært stille så lenge (resten av skanningen). */
const BLOCK_MS = 250;
const MESSAGE_MS = 4000;

/**
 * NumericInput — drop-in erstatning for <input type="number"> til antalls-/kvantitetsfelter
 * pa nettbrett.
 *
 * Problem som loeses:
 * - type="number" gir inkonsistent Enter-oppforsel mellom iOS/Android
 * - Noen Android-tastaturer mangler desimaltegn nar man bruker type="number"
 * - type="number" kan feile med strekkodelesere som sender kontrolltegn
 * - Brukere kan taste ugyldige tegn (bokstaver) som parent-komponenten ma filtrere selv
 * - Norske brukere taster komma, men backend forventer ofte punktum
 *
 * Bruker type="text" + inputMode for riktig virtuelt tastatur. Enter og scanner-input
 * fungerer konsistent.
 *
 * Automatisk input-filtrering:
 * - 'integer': fjerner alle tegn som ikke er 0-9
 * - 'decimal': fjerner alle tegn som ikke er 0-9 eller desimaltegn
 *   - Komma normaliseres til punktum automatisk (norsk tastatur -> backend-format)
 *   - Kun ett desimaltegn tillates (ekstra droppes)
 *
 * Tablet-vennlige defaults:
 *   type="text"                 -> konsistent Enter-oppforsel
 *   inputMode="decimal"/numeric -> numerisk tastatur
 *   pattern                     -> browser-hint for soft-keyboard
 *   enterKeyHint="done"         -> "Ferdig"-knapp som lukker tastaturet
 *
 * Paavirker ikke:
 * - Fysiske strekkodelesere
 * - Global barcode-hook
 *
 * MERK: Verdivalidering utover tegn-filtrering (min/max/steg) ma handteres i
 * onChange/onBlur — type="text" utforer ingen native numerisk validering.
 *
 * Desimaler mot number-state (TW 36382470):
 * Mange parents holder antallet som number og gjoer parseFloat i onChange. Da ble
 * «2,» -> «2.» -> 2 -> re-rendret som «2», og desimaltegnet forsvant foer neste
 * siffer. Komponenten holder derfor en intern utkast-streng mens brukeren taster:
 * sa lenge parseFloat(utkast) tilsvarer value-prop'en vises utkastet («2.», «2.50»),
 * avviker de (parent clampet/avviste/satte programmatisk) vises value. Utkastet
 * nullstilles ved blur. Gjelder kun mode='decimal' med number-value — string-styrte
 * parents og integer-felter er uendret.
 *
 * Antallssperre (prop `quantity`):
 * Overstiger verdien «Maks antall per linje» (SystemSetup.MaxLineItemQuantity), settes
 * feltet tilbake til verdien det hadde før tastesekvensen startet — en skannet EAN
 * fjernes dermed helt, ikke bare siste siffer. Resten av skanningen avvises, og Enter
 * fra skanneren slukes så parent ikke sender inn. Operatøren får en melding under
 * feltet (og vibrasjon der det støttes). Ingen overstyring. Ingen grense satt (NULL/0)
 * = ingen sperre.
 */

const filterIntegerInput = (raw: string): string => {
  return raw.replace(/[^0-9]/g, '');
};

const filterDecimalInput = (raw: string): string => {
  // Normaliser komma til punktum forst
  const normalized = raw.replace(/,/g, '.');
  // Fjern alle tegn som ikke er 0-9 eller punktum
  const cleaned = normalized.replace(/[^0-9.]/g, '');
  // Tillat kun ett punktum — behold forste, dropp resten
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
};

export const NumericInput = forwardRef<HTMLInputElement, NumericInputProps>(
  function NumericInput({ mode = 'decimal', quantity = false, onChange, onBlur, onKeyDown, value, ...props }, ref) {
    const { t } = useTranslation();
    const inputMode = mode === 'integer' ? 'numeric' : 'decimal';
    const pattern = mode === 'integer' ? '[0-9]*' : '[0-9]*[.,]?[0-9]*';

    // Utkast-streng for desimalfelter styrt av en number-value (se doc over)
    const [draft, setDraft] = useState<string | null>(null);
    const useDraft = mode === 'decimal' && typeof value === 'number';

    // Antallssperre (se doc over)
    const maxQuantity = useMaxLineItemQuantity(quantity);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const lastChangeAtRef = useRef(0);
    const burstStartValueRef = useRef('');
    const blockedUntilRef = useRef(0);
    const [message, setMessage] = useState<{ text: string; top: number; left: number; width: number } | null>(null);

    const setRefs = useCallback((el: HTMLInputElement | null) => {
      inputRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
    }, [ref]);

    useEffect(() => {
      if (!message) return;
      const timer = setTimeout(() => setMessage(null), MESSAGE_MS);
      return () => clearTimeout(timer);
    }, [message]);

    const showBlockedMessage = (max: number) => {
      const rect = inputRef.current?.getBoundingClientRect();
      setMessage({
        text: t('common.validation.quantityAboveMax', { '0': max.toLocaleString('nb-NO') },
          `Antallet er høyere enn maks tillatt (${max}). Skannet du en strekkode i antallsfeltet?`),
        top: rect ? rect.bottom + 4 : 16,
        left: rect ? rect.left : 16,
        width: rect ? Math.max(rect.width, 240) : 280,
      });
      try { navigator.vibrate?.(200); } catch { /* ikke støttet */ }
    };

    let displayValue = value;
    if (useDraft && draft !== null) {
      const draftNumber = parseFloat(draft) || 0;
      displayValue = draftNumber === Number(value) ? draft : value;
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const filtered = mode === 'integer' ? filterIntegerInput(raw) : filterDecimalInput(raw);

      // Kun oppdater target.value hvis vi faktisk endret noe — hindrer unodig re-render
      // og bevarer caret-posisjon i normale tilfeller.
      if (filtered !== raw) {
        e.target.value = filtered;
      }

      if (quantity && maxQuantity != null) {
        const now = Date.now();
        // Ny tastesekvens: husk verdien feltet hadde før den startet
        if (now - lastChangeAtRef.current > BURST_GAP_MS) {
          burstStartValueRef.current = displayValue == null ? '' : String(displayValue);
        }
        lastChangeAtRef.current = now;

        const blocked = now < blockedUntilRef.current;
        const numeric = parseFloat(filtered);
        if (blocked || (!isNaN(numeric) && numeric > maxQuantity)) {
          blockedUntilRef.current = now + BLOCK_MS;
          e.target.value = burstStartValueRef.current;
          if (useDraft) setDraft(null);
          if (!blocked) showBlockedMessage(maxQuantity);
          onChange?.(e);
          return;
        }
      }

      if (useDraft) setDraft(filtered);
      onChange?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Enter fra skanneren rett etter en sperre skal ikke sende inn skjemaet/linjen
      if (quantity && e.key === 'Enter' && Date.now() < blockedUntilRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onKeyDown?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (draft !== null) setDraft(null);
      onBlur?.(e);
    };

    return (
      <>
        <input
          ref={setRefs}
          type="text"
          inputMode={inputMode}
          pattern={pattern}
          enterKeyHint="done"
          autoComplete="off"
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          {...props}
          aria-invalid={message ? true : props['aria-invalid']}
        />
        {message && createPortal(
          <div
            role="alert"
            onClick={() => setMessage(null)}
            style={{
              position: 'fixed',
              top: message.top,
              left: message.left,
              width: message.width,
              maxWidth: 'calc(100vw - 32px)',
              zIndex: 10000,
              padding: '8px 12px',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.35,
              background: 'var(--color-btn-danger-end, #f44336)',
              color: '#ffffff',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            }}
          >
            {message.text}
          </div>,
          document.body
        )}
      </>
    );
  }
);
